import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeWhile } from 'rxjs';
import { AppointmentSlot, VisitAppointmentsData } from '../../../features/book-claim/types/visit-appointments.types';
import { AppointmentSlotLabelPipe } from '../../pipes/appointment-slot-label.pipe';
import { TimeFormatPipe } from '../../pipes/time-format.pipe';
import { Breakpoints } from '../../types/breakpoints';
import { detectMobileDevice } from '../../utils/utils';
import { ButtonComponent } from '../button/button.component';
import { CalendarPickerComponent } from '../forms/calendar-picker/calendar-picker.component';
import { CheckboxComponent } from '../forms/checkbox/checkbox.component';
import { LoadingSpinnerComponent } from '../forms/loading-spinner/loading-spinner.component';
import { RadioButtonComponent } from '../forms/radio-button/radio-button.component';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';

type DateString = `${string}-${string}-${string}`;

@Component({
  selector: 'app-appointment-picker',
  imports: [
    ButtonComponent,
    CalendarPickerComponent,
    DatePipe,
    RadioButtonComponent,
    ReactiveFormsModule,
    SkeletonLoaderComponent,
    FormsModule,
    CommonModule,
    CheckboxComponent,
    CommonModule,
    LoadingSpinnerComponent,
    AppointmentSlotLabelPipe,
  ],
  providers: [TimeFormatPipe],
  templateUrl: './appointment-picker.component.html',
  styleUrl: './appointment-picker.component.scss',
})
export class AppointmentPickerComponent implements OnInit, OnDestroy {
  calendarPickerComponent = viewChild<CalendarPickerComponent>('calendarPickerComponent');
  _destroyed = false;
  data = input.required<VisitAppointmentsData | undefined>();
  // slots excluded by slot id
  slotsToExclude = input<string[]>();

  loading = input<boolean>(false);
  disabled = input<boolean>(false);
  availabilityStartDate = input<DateString | undefined>();
  isAllDaySlots = input<boolean>(false);
  isMobile = signal(window.innerWidth < Breakpoints.sm);

  selectedSlot = output<AppointmentSlot | undefined>();
  bringForwardFlagChanged = output<boolean>();
  showBringForwardFlag = input<boolean>(false);
  bringForwardFlag = false;
  selectedAppointment: AppointmentSlot | undefined = undefined;
  selectedDay = signal('');
  appointmentData = output<any>();
  initialAppointmentData = output<any>();
  currentMonthAndYear = signal<string>('');
  selectedDayChange = output<any>();

  showWaitingMessage = false;

  constructor(private readonly injector: Injector) {}

  bringForwardChange(value: boolean) {
    this.bringForwardFlag = value;
    this.bringForwardFlagChanged.emit(value);
  }

  @HostListener('window:resize')
  onResize() {
    const isMobile = window.innerWidth < Breakpoints.sm || detectMobileDevice();
    if (this.isMobile() !== isMobile) {
      this.isMobile.set(isMobile);
    }
  }

  ngOnInit(): void {
    const initialMinDate = this.vm.minDate;
    this.initialAppointmentData.emit({
      minDate: initialMinDate,
      numberOfActiveDatesThisMonth: this.numberOfActiveDatesThisMonth(this.formatDate(initialMinDate)),
      block: '1',
      totalActiveDates: this.vm.activeDates.length.toLocaleString(),
    });
    toObservable(this.loading, { injector: this.injector })
      .pipe(takeWhile(() => !this._destroyed))
      .subscribe((v) => {
        if (v) {
          setTimeout(() => {
            this.showWaitingMessage = true;
          }, 3000);
        } else {
          this.showWaitingMessage = false;
        }
      });

    toObservable(this.slotsToExclude, { injector: this.injector })
      .pipe(takeWhile(() => !this._destroyed))
      .subscribe(() => {
        this.getSlots();
      });
  }

  ngOnDestroy(): void {
    this._destroyed = true;
  }

  protected viewModel = computed(() => {
    const slotsByDate = this.getSlots();
    const activeDates = Object.keys(slotsByDate);
    const firstActiveDate = activeDates[0] as DateString | undefined;
    const lastActiveDate = activeDates.at(-1) as DateString | undefined;
    const availabilityStartDateInput = this.availabilityStartDate();
    const availabilityStartDate = availabilityStartDateInput ?? firstActiveDate;

    return {
      slotsByDate,
      activeDates,
      maxDate: lastActiveDate,
      minDate: firstActiveDate,
      loading: this.loading(),
      morningSlots: this.morningSlots(),
      afternoonSlots: this.afternoonSlots(),
      disabled: this.disabled(),
      isAllDaySlots: this.isAllDaySlots(),
      isMobile: this.isMobile(),
      selectedDay: this.selectedDay(),
      availabilityStartDate,
      calendarPickerComponent: this.calendarPickerComponent(),
    };
  });

  morningSlots = signal<boolean>(false);
  afternoonSlots = signal<boolean>(false);

  setSlotFilter(type: 'morning' | 'afternoon') {
    const slotMap = {
      morning: this.morningSlots,
      afternoon: this.afternoonSlots,
    };

    if (slotMap[type]()) {
      slotMap[type].set(false);
    } else {
      Object.entries(slotMap).forEach(([key, signal]) => {
        signal.set(key === type);
      });
    }

    if (!this.morningSlots() && !this.afternoonSlots()) {
      this.clearSelectedSlot();
    }
  }

  slotValue: string | undefined = undefined;
  selectedDate: string | undefined = undefined;

  get vm() {
    return this.viewModel();
  }

  get hasBringForwardFlag() {
    return this.selectedAppointment?.bringForwardDays;
  }

  clearSelectedSlot(): void {
    this.slotValue = undefined;
    this.selectedSlot.emit(this.slotValue);
  }

  private reduce(a: any, b: any) {
    return {
      ...a,
      [b[0].date]: b,
    };
  }

  getSlots(): Record<string, AppointmentSlot[]> {
    if (!this.data()) return {};
    const slotsByDate = this.data()!.slotsByDate;
    if (!slotsByDate) return {};
    const morningSlots = this.morningSlots();
    const afterNoonSlots = this.afternoonSlots();
    if (!morningSlots && !afterNoonSlots) {
      return Object.values(slotsByDate)
        .map((slots) => {
          return slots
            .map((slot) => {
              if ((this.slotsToExclude() ?? []).includes(slot.id)) return undefined;
              return slot;
            })
            .filter((value) => !!value);
        })
        .filter((value) => value.length > 0)
        .reduce((a, b) => this.reduce(a, b), {});
    }

    if (morningSlots && afterNoonSlots) {
      return slotsByDate;
    }
    const filteredValues: Record<string, AppointmentSlot[]> = Object.values(this.data()!.slotsByDate)
      .map((slots) => {
        return slots
          .map((slot) => {
            const startTimeNumber = parseInt(slot.startTime.replace(/:/g, ''));
            const endTimeNumber = parseInt(slot.endTime.replace(/:/g, ''));

            let validAfternoonSlot = false;
            let validMorningSlot = false;

            if (morningSlots) {
              validMorningSlot = startTimeNumber >= 700 && endTimeNumber <= 1300;
            }

            if (afterNoonSlots) {
              validAfternoonSlot = startTimeNumber >= 1300 && endTimeNumber <= 1800;
            }

            if (validMorningSlot || validAfternoonSlot) {
              return slot;
            }

            return undefined;
          })
          .filter((value) => !!value);
      })
      .filter((value) => value.length > 0)
      .reduce((a, b) => this.reduce(a, b), {});

    if (this.selectedDate) {
      const dateStillValid = Object.keys(filteredValues).includes(this.selectedDate);

      if (!dateStillValid) {
        this.selectedDate = undefined;
        this.slotValue = undefined;
        this.emitSlotValue();
      }

      if (this.slotValue) {
        const slotExists = filteredValues[this.selectedDate!].some((slot: any) => slot.id === this.slotValue);

        if (!slotExists) {
          this.slotValue = undefined;
          this.emitSlotValue();
        }
      }
    }

    return filteredValues;
  }

  setAppointment(id: string) {
    if (id === undefined) return;
    this.slotValue = id;
    const appointment = this.data()!.slotsByDate[this.selectedDate!].find((slot) => slot.id === id);

    this.emitSlotValue(appointment);
  }

  emitSlotValue(appointment: AppointmentSlot | undefined = undefined): void {
    this.selectedAppointment = appointment;
    this.selectedSlot.emit(appointment);
  }

  handleDateSelection(date: string) {
    this.selectedDate = date;
    if (this.showBringForwardFlag()) {
      this.bringForwardFlag = false;
      this.bringForwardFlagChanged.emit(this.bringForwardFlag);
    }

    if (this.vm.isAllDaySlots) {
      this.slotValue = this.vm.slotsByDate[date]?.[0]?.id;
      this.setAppointment(this.slotValue);
    } else {
      const slotsForDate = this.vm.slotsByDate[date] ?? [];
      if (slotsForDate.length === 1) {
        this.slotValue = slotsForDate[0].id;
        this.setAppointment(this.slotValue);
      } else {
        this.slotValue = undefined;
        this.emitSlotValue();
      }
    }
    this.selectedDay.set(date);
    this.selectedDayChange.emit({
      selectedDay: date,
      numberOfSlotsForSelectedDay: this.numberOfSlotsForSelectedDay,
    });
    this.scrollTimeslotIntoView();
  }

  selectEarliestDate(): void {
    const earliest = this.vm.minDate;
    if (!earliest) return;
    this.selectedDate = earliest;
    this.handleDateSelection(earliest);
  }

  numberOfActiveDatesThisMonth(currentMonthAndYear: string): string {
    let [yearString, monthString] = currentMonthAndYear.split('_');
    const year = parseInt(yearString, 10);
    const month = parseInt(monthString, 10);

    return this.vm.activeDates
      .filter((dateString) => {
        const date = new Date(dateString);
        return date.getFullYear() === year && date.getMonth() + 1 === month;
      })
      .length.toLocaleString();
  }

  get block() {
    let [yearString, monthString] = this.currentMonthAndYear().split('_');
    const currentYear = parseInt(yearString, 10);
    const currentMonth = parseInt(monthString, 10);
    const minDateString = this.vm.minDate;
    if (!minDateString || Number.isNaN(currentYear) || Number.isNaN(currentMonth)) {
      return '0';
    }
    const minDate = new Date(minDateString);
    const current = new Date(currentYear, currentMonth - 1);

    const monthDifference = (startDate: Date, endDate: Date) =>
      (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());

    return (monthDifference(minDate, current) + 1).toLocaleString();
  }

  get numberOfSlotsForSelectedDay() {
    const day = this.vm.selectedDay || '';
    if (day) {
      return this.vm.slotsByDate[day].length.toLocaleString();
    }
    return '';
  }

  setAppointmentData(date: any) {
    this.currentMonthAndYear.set(date);
    this.appointmentData.emit({
      minDate: this.vm.minDate,
      numberOfSlotsForSelectedDay: this.numberOfSlotsForSelectedDay,
      currentMonthAndYear: this.currentMonthAndYear(),
      numberOfActiveDatesThisMonth: this.numberOfActiveDatesThisMonth(this.currentMonthAndYear()),
      block: this.block,
      totalActiveDates: this.vm.activeDates.length.toLocaleString(),
    });
  }

  formatDate(dateString: string | undefined) {
    if (!dateString) {
      return '';
    }
    const [year, month] = dateString.split('-');
    return `${year}_${parseInt(month, 10)}`;
  }

  scrollTimeslotIntoView() {
    setTimeout(() => {
      document.getElementById('slots')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 200);
  }
}
